/**
 * Field Tooltips for Config Registry
 * Centralized tooltip descriptions for all form fields
 * 
 * Usage:
 *   import { FIELD_TOOLTIPS, getLookupTooltip } from '../constants/fieldTooltips';
 *   
 *   // For static fields:
 *   FIELD_TOOLTIPS.configKey
 *   
 *   // For dynamic lookup fields:
 *   getLookupTooltip('COMPONENT')
 */

// Static field tooltips
export const FIELD_TOOLTIPS: Record<string, string> = {
  // Basic Information
  configKey: 'Unique identifier for this configuration. Use snake_case format (e.g., my_config_key). This cannot be changed after creation.',
  displayName: 'Human-readable name shown in the UI. Make it descriptive and clear.',
  configDocumentation: 'Detailed description of what this configuration does, when to use it, and any important notes.',
  configType: 'Category of configuration (e.g., email_template, ingestion, frontend_configs). Determines where this config is used.',
  
  // Values & Behavior
  defaultValue: 'The default value used when no override is specified. For JSON values, enter valid JSON format.',
  possibleValues: 'List of possible values for this configuration. These are the values that can be used when setting this config.',
  validationRule: 'JavaScript expression to validate values. Use "value" variable (e.g., value > 0 && value < 100).',
  validationExpression: 'JavaScript expression to validate values. Use "value" variable (e.g., value > 0 && value < 100).',
  configDataType: 'The data type of this configuration value. Determines how values are validated and displayed.',
  configRiskLevel: 'Classifies how sensitive this config is. Drives which roles can view, edit, or are blocked entirely from seeing it in Project Configs and the Inspector Panel (DA-2161). Does not restrict this admin screen.',

  // Flags & Metadata
  deprecated: 'Mark as deprecated if this configuration should no longer be used. Deprecated configs are hidden from new templates.',
  isMandatoryField: 'If enabled, this configuration must have a value in every template where it appears.',
  utilityConfigurationRequired: 'If enabled, this configuration requires utility-level setup before use.',
  specialHandlingRequired: 'If enabled, this configuration requires special handling or custom processing logic.',
  
  // Relationships
  parentConfig: 'Parent configuration that this config inherits from. Child configs inherit context from their parent.',
  relatedConfigs: 'Other configurations that are related but not in a parent-child relationship.',
  duplicateConfigs: 'Configurations that are duplicates or aliases of this one. Used for migration tracking.',
  dependsUpon: 'Configurations that must be set before this one can be used.',
};

// Dynamic lookup field tooltips - keyed by lookup type code
export const LOOKUP_TOOLTIPS: Record<string, string> = {
  CONFIG_TYPE: 'The category this configuration belongs to. Determines which templates can use this config.',
  COMPONENT: 'The system component or module that uses this configuration.',
  SECTION: 'The section within the component where this configuration is applied.',
  CATEGORIZATION: 'Categorization level for organizing and filtering configurations.',
  FILE_TYPE: 'File types that this configuration applies to (for file-based configs).',
  DATA_SOURCE: 'The data source this configuration is associated with.',
};

/**
 * Get tooltip for a lookup field by its type code
 * Falls back to a generic message if no specific tooltip exists
 */
export const getLookupTooltip = (lookupTypeCode: string): string => {
  return LOOKUP_TOOLTIPS[lookupTypeCode] || `Select a value for ${lookupTypeCode.toLowerCase().replace(/_/g, ' ')}`;
};

/**
 * Get tooltip for any field (static or dynamic)
 */
export const getFieldTooltip = (fieldKey: string): string | undefined => {
  // Check static tooltips first
  if (FIELD_TOOLTIPS[fieldKey]) {
    return FIELD_TOOLTIPS[fieldKey];
  }
  // Check if it's a lookup type
  if (LOOKUP_TOOLTIPS[fieldKey]) {
    return LOOKUP_TOOLTIPS[fieldKey];
  }
  return undefined;
};

// Column header tooltips for tables
export const COLUMN_TOOLTIPS: Record<string, string> = {
  // Config Registry Table
  serialNo: 'Sequential number for this row in the current page',
  configKey: 'Unique identifier for the configuration',
  displayName: 'Human-readable name of the configuration',
  configType: 'Category or type of configuration',
  configDataType: 'Data type of the configuration value (STRING, INTEGER, JSON, etc.)',
  verificationStatus: 'Current verification status of the configuration (TODO, READY_FOR_QA, APPROVED, REJECTED)',
  approvalStatus: 'Current approval status of the configuration (deprecated - use verificationStatus)',
  component: 'System component or module that uses this configuration',
  configDocumentation: 'Description and usage notes for this configuration',
  defaultValue: 'Default value when no override is specified',
  configClassification: 'Classification level for organizing configurations',
  version: 'Version number of this configuration',
  actions: 'Available actions for this configuration',

  // Templates Table
  templateName: 'Name of the template',
  name: 'Name of the template',
  description: 'Description of what this template is used for',
  module: 'Module or system area this template belongs to',
  numConfigs: 'Number of configurations in this template',

  // Template Detail Table (Configs in Template)
  templateValue: 'Value set for this configuration in the template (overrides default)',

  // Add Configs Dialog
  select: 'Select configs to add to the template',
};

/**
 * Get tooltip for a column header
 */
export const getColumnTooltip = (columnKey: string): string | undefined => {
  return COLUMN_TOOLTIPS[columnKey];
};

